import { CoexistenceEntryService } from './services/coexistence-entry.service';
import { coexistenceEntryNotice } from './services/coexistence-entry-link';
import { provideHttpClient, withInterceptorsFromDi, HttpHeaders } from '@angular/common/http';
import { EnvironmentProviders, inject, Injector, Provider } from '@angular/core';
import { provideApollo } from 'apollo-angular';
import { setContext, SetContextLink } from '@apollo/client/link/context';
import { ErrorLink, onError } from '@apollo/client/link/error';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { ApolloLink, DefaultOptions, InMemoryCache, Observable, split } from '@apollo/client/core';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { createClient } from 'graphql-ws';
import { HttpLink } from 'apollo-angular/http';
import { HttpLink as ApolloHttpLink } from '@apollo/client/link/http';
import { buildCache } from './graphql-cache';
import { usesBearerTokenAuth } from './auth-mode';
import { SessionService } from './services/session.service';
import type { SessionRefreshOutcome } from './services/session-refresh';
import { TeamsService } from './services/teams.service';
import { refreshUnavailableError, sessionExpiredError } from './services/apollo-error';
import {
  LOGIN_OPERATIONS,
  SOCKET_CONTEXT_KEY,
  hasUnauthorizedGraphQLError,
  httpStatusOf,
  isBackgroundOperation,
  shouldLogoutAfterAuthFailure,
  shouldMarkOffline,
  shouldRefreshOnHttp401,
} from './services/graphql-auth-helpers';
import {
  handleWsClosed,
  realtimeWsConnected$,
  realtimeWsFailed$,
} from './services/realtime-connection';
import { wsHostOf } from './services/ws-config-health';
import { recoverWsOnBfcacheRestore } from './services/ws-bfcache';
import { recoverSubscriptionAuth, subscriptionCredentialRecovery } from './services/ws-auth-recovery';
import { realtimeRetryOptions } from './services/realtime-retry';
import { CachePersistService } from './services/cache-persist.service';
import { isTauri, tauriFetchFn } from './tauri-runtime';
import { getApiUrl, getWsUrl } from './region';
import { environment } from '../../environments/environment';
import { isAdminPreviewSession } from './guards/admin-preview';
import { isFlowJoinSurface } from './utils/flow-join-surface';
import { previewBackendGate } from './preview-backend-gate';

// Read from the environment (same as auth.guard.ts / session.service.ts) so a
// rotated NG_APP_SPACES_CLIENT_ID applies everywhere — no hardcoded divergence.
const SPACES_CLIENT = environment.SPACES_CLIENT_ID;

const loginOperations = LOGIN_OPERATIONS;

/**
 * Renew the credential and report which of the three things happened.
 *
 * Deliberately side-effect-FREE on failure: the logout decision belongs to the
 * caller, because it depends on whether THAT caller was user-initiated or a
 * background poll. When this function logged out itself, a background request
 * that happened to win the race destroyed the session for everyone.
 *
 * Coalescing lives in `SessionService.refreshSession()` rather than here, so
 * the route guards' refresh and the link chain's refresh share one in-flight
 * request. The module-level single-flight this replaced only ever covered
 * Apollo, so a cold page load could still spend two slots of the API's per-IP
 * refresh budget.
 */
async function renewCredential(session: SessionService): Promise<SessionRefreshOutcome> {
  try {
    return await session.refreshSession();
  } catch {
    return 'unavailable';
  }
}

/**
 * SHA-256 helper for Automatic Persisted Queries (APQ).
 *
 * Hashes the GraphQL query text so the client can send a 64-character hash
 * over the wire instead of the full query body. Uses the browser SubtleCrypto
 * API — no extra dependency required.
 */
async function sha256(text: string): Promise<string> {
  const buf = new TextEncoder().encode(text);
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** Wraps the request chain to mark the API as online whenever a response
 *  resolves (data OR errors — both mean we reached the server). Errors that
 *  bubble up here without a response are network failures; the existing
 *  ErrorLink picks those up and calls `markOffline()`. */
function makeOnlineProbe(session: SessionService): ApolloLink {
  return new ApolloLink((operation, forward) => {
    return new Observable<ApolloLink.Result>((observer) => {
      const sub = forward(operation).subscribe({
        next: (result) => {
          session.markOnline();
          observer.next(result);
        },
        error: (err) => observer.error(err),
        complete: () => observer.complete(),
      });
      return () => sub.unsubscribe();
    });
  });
}

/**
 * Refresh the access token once, then replay the operation. Shared by BOTH
 * the GraphQL-body `UNAUTHORIZED` path and the bare-HTTP-401 path so a stale
 * token recovers identically however the server signals it. Emits a terminal
 * coded UNAUTHORIZED (not a bare Error) when refresh fails so the data layer
 * treats it as an expected session expiry (drives logout → login, excluded
 * from Error Tracking).
 *
 * `background` (a poll / prefetch / stale-while-revalidate refresh) changes
 * ONLY the failure tail: the refresh is still attempted, but when it fails the
 * session is left intact and the caller just gets the coded UNAUTHORIZED to
 * degrade against. A user who is actively working is never ejected by a
 * request they didn't make.
 *
 * A refresh that reached no VERDICT (`unavailable` — rate-limited, gateway,
 * offline) never ends the session either, user-initiated or not. It surfaces as
 * a retryable network-class failure instead, because that is what it is: the
 * cookie was not rejected, we just could not renew it. Treating it as expiry is
 * how a shared office egress IP exhausting the API's 10-per-minute refresh
 * bucket signed people out of a session that was never broken.
 */
function retryAfterRefresh(
  operation: ApolloLink.Operation,
  forward: (op: ApolloLink.Operation) => Observable<ApolloLink.Result>,
  session: SessionService,
  logout?: () => Promise<void>,
  background = false,
): Observable<ApolloLink.Result> {
  return new Observable<ApolloLink.Result>((observer) => {
    renewCredential(session)
      .then(async (outcome) => {
        if (outcome !== 'refreshed') {
          const expired = outcome === 'expired';
          if (expired && !background) await logout?.();
          observer.error(expired ? sessionExpiredError() : refreshUnavailableError());
          return;
        }
        operation.setContext((ctx: Record<string, unknown>) => ({
          ...ctx,
          __retriedAfterRefresh: true,
        }));
        // NOTE: Apollo's ErrorLink subscribes the observable its handler
        // returns DIRECTLY to the caller — a retried operation never re-enters
        // the handler. So the `__retriedAfterRefresh` branches up in the
        // handler can't see this second attempt; the replay's own outcome has
        // to be judged here.
        forward(operation).subscribe({
          next: (result) => {
            // GraphQL-body UNAUTHORIZED on the replay: the freshly-minted
            // token is still rejected, so the session really is over — but
            // only a request the user made may say so. Pass the result
            // through untouched either way.
            if (!background && hasUnauthorizedGraphQLError(result)) void logout?.();
            observer.next(result);
          },
          error: (replayError: unknown) => {
            if (httpStatusOf(replayError) === 401) {
              if (!background) void logout?.();
              observer.error(sessionExpiredError());
              return;
            }
            observer.error(replayError);
          },
          complete: observer.complete.bind(observer),
        });
      })
      .catch((err) => observer.error(err));
  });
}

/**
 * Build the auth/recovery ErrorLink. Exported (rather than file-private) so
 * `graphql-error-link.spec.ts` can drive the real link with a stub session and
 * assert the background-vs-user-initiated 401 behaviour end to end.
 */
export function withTokenFactory(session: SessionService, logout?: () => Promise<void>) {
  const errorLink = new ErrorLink(({ error, operation, forward }) => {
    const opName = operation.operationName?.toLowerCase?.() ?? '';
    const opContext = operation.getContext() as Record<string, unknown> | undefined;
    const alreadyRetried = !!opContext?.['__retriedAfterRefresh'];
    // A poll / prefetch / silent revalidation the user never asked for. It may
    // still refresh the token, but it must never destroy the session.
    const background = isBackgroundOperation(opContext, opName);
    const mayLogout = shouldLogoutAfterAuthFailure(opContext, opName);

    // ── GraphQL errors (server returned a parseable response) ──
    if (CombinedGraphQLErrors.is(error)) {
      // The HTTP request actually completed — server is up.
      session.markOnline();

      // Vendor routes handle auth independently — don't try employee refresh
      if (window.location.hash.startsWith('#/vendor')) return undefined;

      for (const err of error.errors) {
        if ((err as any).extensions?.code === 'UNAUTHORIZED') {
          if (opName && loginOperations.includes(opName)) return undefined;
          if (alreadyRetried) {
            // Refresh + replay already failed. Only a user-initiated request
            // may end the session; a background one degrades quietly.
            if (mayLogout) void logout?.();
            return undefined;
          }
          return retryAfterRefresh(operation, forward, session, logout, background);
        }
      }
      return undefined;
    }

    // ── Bare HTTP 401 (no GraphQL body) — e.g. a stale Bearer token rejected
    //    at the HTTP/auth layer before the GraphQL resolver runs. The server
    //    DID respond (so we're online); refresh the token + replay once,
    //    exactly like the GraphQL UNAUTHORIZED path. Without this the request
    //    fell through to the offline branch and surfaced a raw
    //    "Http failure response … 401" to the user (e.g. the Scope explorer
    //    `policies` query). Vendor routes manage their own auth. ──
    const status = httpStatusOf(error);
    if (status === 401 && !window.location.hash.startsWith('#/vendor')) {
      session.markOnline();
      if (shouldRefreshOnHttp401(status, opName, alreadyRetried)) {
        return retryAfterRefresh(operation, forward, session, logout, background);
      }
      // Already retried once (or a login op) → expire the session cleanly,
      // but ONLY for a request the user actually made. A background poll that
      // still 401s after a refresh leaves the session alone.
      if (alreadyRetried) {
        if (mayLogout) void logout?.();
        return undefined;
      }
    }

    // ── Definite HTTP responses that aren't handled above ──
    // Any status at all means the server was reached: a GraphQL-validation
    // 400 (schema skew), a 403, a 500 with a body… none of these are an
    // outage. Flipping the offline banner here used to make "visiting a
    // page whose query the deployed schema doesn't know yet" look like the
    // whole API was unreachable (July 2026 home-feed incident).
    if (!shouldMarkOffline(status)) {
      session.markOnline();
      return undefined;
    }

    // ── Network errors (request never reached the server — connection
    //    refused, DNS fail, timeout, CORS-blocked) or gateway-level
    //    502/503/504 (the LB answered; the API behind it didn't) ──
    //
    // Keep the session intact and surface the offline state so the connection
    // banner appears. The user keeps their tokens; when the API comes back,
    // future requests resolve normally and `markOnline` dismisses the banner.
    // This stops a dev API restart from logging the user out.
    session.markOffline();
    return undefined;
  });

  return errorLink;
}

/**
 * The per-request default headers, and THE ONE PLACE THAT DECIDES WHETHER A
 * BEARER GOES ON THE WIRE.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * THE 401 ON EVERY PAGE LOAD
 * ─────────────────────────────────────────────────────────────────────────
 *
 * This link used to attach `Authorization: Bearer <localStorage['access-token']>`
 * to every employee request on every surface, and nothing consulted the rule
 * that decides how the surface actually authenticates.
 *
 *   `auth-mode.ts`  usesBearerTokenAuth = isInTeams || isAddinSurface()
 *                   — Teams and the Outlook add-in carry a Bearer; WEB and
 *                     TAURI authenticate with the HTTP-only cookie.
 *   `auth.guard.ts` returns early once the cookie validates, so
 *                   `refreshAccessToken()` never runs on the healthy path and
 *                   nothing keeps `access-token` fresh on the cookie surfaces.
 *
 * So an ordinary web session — live cookie, STALE leftover `access-token` from
 * some earlier bearer flow — sent a dead Bearer. The gateway rejected it
 * before any resolver ran, `ErrorLink` refreshed once and replayed, and the
 * page then worked: one logged `POST /graphql 401 (Unauthorized)` per
 * operation already in flight, on a session that was never actually broken.
 *
 * The fix is not to swallow the log and not to refresh harder. It is to STOP
 * SENDING A CREDENTIAL THE SURFACE DOES NOT AUTHENTICATE WITH. On web and
 * Tauri the cookie rides along on `withCredentials` / the Tauri cookie jar and
 * is the whole story; the Bearer was never doing any work there, it was only
 * ever able to do harm.
 *
 * ⚠ TEAMS AND THE ADD-IN MUST KEEP IT. There is no cookie in a Teams iframe or
 * an Outlook task pane — `TeamsAuthService` / `AddinAuthService` mint the
 * access token and this header is the only thing carrying it. Dropping it
 * there logs those two surfaces out entirely, which is why the predicate is
 * INJECTED rather than re-derived: `usesBearerTokenAuth` is the same function
 * `auth.guard.ts` and `admin.guard.ts` ask, so the three cannot drift.
 *
 * THE VENDOR BRANCH IS UNTOUCHED AND UNCONDITIONAL. `#/vendor` is a separate
 * portal with its own JWT in `vendor-session` and no cookie of any kind — it
 * is not the employee bearer/cookie question wearing a different hash, and
 * gating it on `usesBearerTokenAuth` would sign every vendor out.
 *
 * `bearerSurface` is a THUNK, evaluated per request. `TeamsService.initialize()`
 * is an APP_INITIALIZER and this link is constructed while providers are still
 * being built, so reading the flag once at construction time would bake in
 * "not Teams" for the whole session. `isAddinSurface()` reads the hash, which
 * also changes under the app's feet.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * SPLIT IN TWO, AND THE PURE HALF IS THE ONE WITH THE RULE IN IT
 * ─────────────────────────────────────────────────────────────────────────
 *
 * `defaultRequestHeaders` takes the whole world as an argument — the hash, the
 * auth mode, the three stored values — and returns a plain object. It touches
 * no `window`, no `localStorage` and no Angular, so a spec can state "web with
 * a stale access-token sends NO Authorization" and "Teams with the same token
 * sends it" as two direct calls rather than by mounting a link chain and
 * inspecting an operation context. A rule that is hard to assert is a rule
 * that gets asserted approximately.
 */
export interface DefaultHeaderInputs {
  /** `window.location.hash` — the vendor portal is chosen by route. */
  readonly hash: string;
  /** `usesBearerTokenAuth(isInTeams)`, resolved by the caller. */
  readonly bearerSurface: boolean;
  /** `localStorage['access-token']`. */
  readonly accessToken: string | null;
  /** `localStorage['vendor-session']`, raw JSON. */
  readonly vendorSession: string | null;
  /** `localStorage['tenant']`. */
  readonly tenant: string | null;
}

export function defaultRequestHeaders(input: DefaultHeaderInputs): Record<string, string> {
  const headers: Record<string, string> = { spaces_client: SPACES_CLIENT };

  if (input.hash.startsWith('#/vendor')) {
    // Vendor portal: use vendor JWT from vendor-session
    try {
      if (input.vendorSession) {
        const { token } = JSON.parse(input.vendorSession);
        if (token) headers['Authorization'] = `Bearer ${token}`;
      }
    } catch { /* ignore malformed session */ }
    return headers;
  }

  // Employee app: the bearer ONLY on the surfaces that authenticate with one.
  // See the docblock — on web and Tauri the cookie is the credential and a
  // stale `access-token` here is the 401 on every page load.
  if (input.bearerSurface && input.accessToken) {
    headers['Authorization'] = `Bearer ${input.accessToken}`;
  }
  if (input.tenant) {
    headers['spaces_tenant'] = input.tenant;
  }
  return headers;
}

export interface SubscriptionParamInputs
  extends Pick<DefaultHeaderInputs, 'bearerSurface' | 'accessToken' | 'tenant'> {
  /** `isTauri`: the desktop app, whose socket cannot carry the session cookie. */
  readonly desktop: boolean;
}

/**
 * The `connection_init` payload, built afresh for every socket, so a
 * reconnect always reads the token as it is now.
 *
 * The token goes only where no cookie can:
 * - **Web** sends none. The upgrade request carries the session cookie, and a
 *   leftover bearer must not ride beside it (the rule `defaultRequestHeaders`
 *   documents).
 * - **Teams and the Outlook add-in** send their bearer, as on HTTP.
 * - **Desktop** sends its id token. Its session cookies live in the Tauri HTTP
 *   plugin's jar, and the webview opens the socket itself with its own empty
 *   jar, so a desktop socket without the token is anonymous: every
 *   subscription was refused, and notifications, Home and booking changes
 *   never streamed. `access-token` is the id token the desktop already keeps
 *   for this (`adoptLoginHandoff`, the auth guard's relaunch mint, the 45-minute
 *   keepalive in `DesktopAuthService`, and every cookie refresh).
 *
 * The desktop also names the tenant its token was issued under, read from the
 * token's own claim. Nothing writes `localStorage['tenant']` on desktop, and the
 * API refuses an id token that arrives without one. The claim is only read: the
 * API verifies the token against that tenant, so a wrong one is refused.
 */
export function defaultSubscriptionParams(input: SubscriptionParamInputs): Record<string, string> {
  const params: Record<string, string> = { spaces_client: SPACES_CLIENT };
  const sendsToken = input.bearerSurface || input.desktop;
  if (sendsToken && input.accessToken) params['spaces_session'] = input.accessToken;
  const tenant = input.desktop && input.accessToken
    ? (idTokenTenant(input.accessToken) ?? input.tenant)
    : input.tenant;
  if (tenant) params['spaces_tenant'] = tenant;
  return params;
}

/**
 * The Identity Platform tenant an id token was issued under (its
 * `firebase.tenant` claim), or null for anything that is not such a token.
 * Decoded, never verified; the API does the verifying.
 */
export function idTokenTenant(token: string): string | null {
  const payload = token.split('.')[1];
  if (!payload) return null;
  try {
    const claims: unknown = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    const tenant = (claims as { firebase?: { tenant?: unknown } } | null)?.firebase?.tenant;
    return typeof tenant === 'string' && tenant.length > 0 ? tenant : null;
  } catch {
    // Not base64 or not JSON: not an id token, so there is no tenant to read.
    return null;
  }
}

function makeDefaultsLink(bearerSurface: () => boolean): ApolloLink {
  return new SetContextLink((prevContext) => {
    const merged = headersToRecord(prevContext?.headers);
    Object.assign(
      merged,
      defaultRequestHeaders({
        hash: window.location.hash,
        bearerSurface: bearerSurface(),
        accessToken: localStorage.getItem('access-token'),
        vendorSession: localStorage.getItem('vendor-session'),
        tenant: localStorage.getItem('tenant'),
      }),
    );
    // Two HTTP links, two header shapes. apollo-angular's HttpLink (web) reads
    // an Angular `HttpHeaders`; @apollo/client's HttpLink (desktop, Tauri
    // fetch) spreads a plain object — hand it an `HttpHeaders` instance and it
    // has no enumerable keys, so every header (spaces_client included) vanishes
    // and the API answers "Invalid Client" to everything.
    // apollo-angular augments `OperationContext.headers` as `HttpHeaders`;
    // the desktop link never sees that type, so the cast only quiets the
    // augmentation.
    if (isTauri) return { headers: merged as unknown as HttpHeaders };
    let headers = new HttpHeaders();
    for (const [name, value] of Object.entries(merged)) {
      headers = headers.set(name, value);
    }
    return { headers };
  });
}

/** Flatten whatever a previous link left in `context.headers` to a record. */
function headersToRecord(prev: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (prev instanceof HttpHeaders) {
    for (const name of prev.keys()) out[name] = prev.get(name) ?? '';
  } else if (prev && typeof prev === 'object') {
    for (const [name, value] of Object.entries(prev as Record<string, unknown>)) {
      if (typeof value === 'string') out[name] = value;
    }
  }
  return out;
}

// Lazily-built singleton cache. Initialised on first call to `getApolloCache()`,
// which is invoked both by the Apollo factory and by the boot-time hydrator.
// This is the smallest piece of shared state needed to bridge the two —
// Angular's `provideApollo` doesn't otherwise expose the cache instance.
let cacheInstance: InMemoryCache | null = null;
function getApolloCache(): InMemoryCache {
  if (!cacheInstance) cacheInstance = buildCache();
  return cacheInstance;
}

function createApollo(httpLink: HttpLink) {
  const injector = inject(Injector);
  const session = inject(SessionService);
  // The surface's auth mode, asked at REQUEST time rather than now — see
  // `makeDefaultsLink`. `TeamsService` is already in this graph transitively
  // (`SessionService` injects it), so this adds no new construction order.
  const teams = inject(TeamsService);
  const withDefaults = makeDefaultsLink(() => usesBearerTokenAuth(teams.isInTeams()));
  const logout = async () => {
    // Public surfaces that intentionally do not require auth — never force a
    // login redirect from a background 401, otherwise opening one of these
    // pages while signed-out bounces the user straight to login.
    if (window.location.hash.startsWith('#/vendor')) return;
    // The old design-system gallery URL: now a hop that leaves for the Ricoh
    // Paper docs site (core/guards/paper-docs-redirect.ts).
    if (window.location.hash.startsWith('#/design-system')) return;
    // Public preview harnesses (schedule redesign, panel states, community
    // apps, …) render synthetic data with no session — never bounce them.
    if (window.location.hash.startsWith('#/preview/')) return;
    // The screen that exists BECAUSE no session could be established. A 401
    // here is the expected state, not news, and acting on it would restart the
    // cycle this page ended.
    if (window.location.hash.startsWith('#/sign-in-problem')) return;
    // A hosted meeting's join page: a guest has no session at all, and "not
    // signed in" there means guest, never a trip to sign-in (P7 design 8.1).
    if (isFlowJoinSurface(window.location.hash)) return;
    // Signage runs on unattended displays with NO user session — a background
    // 401 (screen-link data is token-authed, not user-authed) must never
    // bounce the display to the login page.
    if (window.location.hash.startsWith('#/signage')) return;
    // Outlook add-in pane: auth is owned by AddinAuthService (silent NAA +
    // in-pane sign-in button) — a background logout would fight it.
    if (window.location.hash.startsWith('#/addin')) return;
    // Dev-only admin preview renders the admin shell off mock data with no
    // session (see admin-preview.ts). Its unauthenticated reads 401 by design,
    // so bouncing to login defeats the whole bypass — `?adminPreview=1` routes
    // never reached their component.
    if (isAdminPreviewSession()) return;
    // Counted, not unconditional: a 401 the user's own request earned still
    // ends the session, but if this browser has already been round to login-web
    // and come back to the same 401, the next lap is a loop and it stops with
    // an explanation instead (`auth-recovery.ts`).
    await session.endExpiredSession();
  };

  // In Tauri the webview origin is `tauri://localhost`, so cookies from
  // login-web won't transfer cross-origin. The Tauri HTTP plugin routes
  // the request through Rust and keeps its own cookie jar — same backend,
  // same auth flow, no CORS preflight. Web stays on Angular HttpClient.
  // `uri` is a function so the desktop build resolves the *selected region's*
  // API at request time (getApiUrl); web returns environment.graph unchanged.
  const http = isTauri
    ? new ApolloHttpLink({
        uri: () => getApiUrl(),
        fetch: tauriFetchFn,
        credentials: 'include',
      })
    : httpLink.create({
        uri: () => getApiUrl(),
        withCredentials: true,
      });

  // Automatic Persisted Queries — sends a SHA-256 hash of the query in place of
  // the full query body. On cache miss the server responds with
  // `PersistedQueryNotFound`; the link auto-retries with the full query and the
  // server caches the hash. POST is used (not GET) because some intermediaries
  // strip the Authorization header on GET requests.
  const persistedLink = createPersistedQueryLink({ sha256, useGETForHashedQueries: false });

  const ownershipNotice = coexistenceEntryNotice(locationId => {
    if (environment.COEXISTENCE_ENTRY_ENABLED) void injector.get(CoexistenceEntryService).refresh(locationId);
  });
  const httpChain = ApolloLink.from([
    ownershipNotice,
    withDefaults,
    withTokenFactory(session, logout),
    makeOnlineProbe(session),
    persistedLink,
    http,
  ]);

  // WebSocket link for GraphQL subscriptions (graphql-ws protocol)
  const wsClient = createClient({
    url: () => getWsUrl(),
    connectionParams: () => defaultSubscriptionParams({
      bearerSurface: usesBearerTokenAuth(teams.isInTeams()),
      desktop: isTauri,
      accessToken: localStorage.getItem('access-token'),
      tenant: localStorage.getItem('tenant'),
    }),
    ...realtimeRetryOptions,
    on: {
      connected: () => {
        console.log('[WS] Connected');
        // Notify RealtimeService so it can full-refetch after a reconnect
        // (recovers events missed while the socket was down).
        realtimeWsConnected$.next();
      },
      // `closed` and `error` also feed analytics, for the failure modes
      // graphql-ws can see: rejected handshake, auth close, drop. A WebSocket
      // constructor throw (mixed content) never reaches these handlers — see
      // the note in realtime-connection.ts. Host only; never the URL.
      //
      // `handleWsClosed` decides whether this close is an outage or the lazy
      // client tidying up after itself. Do NOT push to `realtimeWsDisconnected$`
      // from here: firing it on a normal 1000 close is what put a "Connection
      // problem" banner over a healthy schedule, and left no telemetry saying so.
      closed: (event) => {
        const close = event as Partial<CloseEvent> | undefined;
        handleWsClosed({
          kind: 'closed',
          host: wsHostOf(getWsUrl()),
          code: close?.code,
          reason: close?.reason,
        });
        console.log('[WS] Disconnected');
      },
      error: (err: unknown) => {
        realtimeWsFailed$.next({
          kind: 'error',
          host: wsHostOf(getWsUrl()),
          message:
            err instanceof Error
              ? err.message
              : typeof err === 'string'
                ? err
                : undefined,
        });
        console.warn('[WS] Error:', err);
      },
    },
  });

  // A bfcached page is handed back with a dead socket and no event to say so.
  // See `ws-bfcache.ts` for why this is `pageshow`-only and why `terminate()`
  // is the right call rather than a reconnect.
  recoverWsOnBfcacheRestore(wsClient);

  const recoverSubscriptionCredential = subscriptionCredentialRecovery(
    () => renewCredential(session),
    () => wsClient.terminate(),
  );
  const wsAuthLink = new ApolloLink((operation, forward) =>
    recoverSubscriptionAuth(() => forward(operation), recoverSubscriptionCredential),
  );
  const wsLink = ApolloLink.from([wsAuthLink, new GraphQLWsLink(wsClient)]);

  // Split: subscriptions → WS, queries/mutations → HTTP — unless the call
  // asks for the socket (`SOCKET_CONTEXT_KEY`: the live Kickabout relay).
  // In front of both: a preview harness with no session answers the shell's
  // reads itself and opens no socket (`preview-backend-gate.ts`).
  const link = ApolloLink.from([
    previewBackendGate(),
    split(
      ({ query, getContext }) => {
        const def = getMainDefinition(query);
        if (getContext()[SOCKET_CONTEXT_KEY] === true) return true;
        return def.kind === 'OperationDefinition' && def.operation === 'subscription';
      },
      wsLink,
      httpChain,
    ),
  ]);

  const defaultOptions: DefaultOptions = {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'cache-first',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  };

  return {
    cache: getApolloCache(),
    link,
    defaultOptions,
  };
}

/**
 * Hydrate the Apollo cache from persisted storage on app boot.
 *
 * Resolves once the cache has been restored (or immediately if nothing was
 * persisted). Safe to await behind a short timeout race in `app.config.ts` —
 * persistence still continues in the background past the timeout.
 */
export function hydrateApolloCache(persist: CachePersistService): Promise<void> {
  return persist.hydrate(getApolloCache());
}

export function provideGraphQL(): Array<Provider | EnvironmentProviders> {
  return [
    provideHttpClient(withInterceptorsFromDi()),
    provideApollo(() => {
      const httpLink = inject(HttpLink);
      return createApollo(httpLink);
    }),
  ];
}
